import React, { useEffect, useState } from 'react';
import './EventList.css';
import supabase from "../../../supabaseClient";
import { useAuth } from '../../../contexts/authContext';

const EventList = ({ activeTab, setSelectedEvent }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    
    const fetchEvents = async () => {
      try {
        let eventList = [];
        
        if (activeTab === 'invited') {
          // Fetch events the user is invited to
          const { data, error } = await supabase
            .from('event_invites')
            .select('event_id, events:events!event_id(*)')
            .eq('invitee_id', user.id);

          if (error) throw error;
          eventList = data.map(invite => invite.events).filter(Boolean);

        } else if (activeTab === 'going') {
          // Fetch events the user is going to
          const { data, error } = await supabase
            .from('event_attendees')
            .select('event_id, events:events!event_attendees_event_id_fkey(*)')
            .eq('user_id', user.id)
            .eq('role', 'going');
            
          if (error) throw error;
          eventList = data.map(att => att.events).filter(Boolean);
          
        } else if (activeTab === 'interested') {
          // Fetch events the user is interested in
          const { data, error } = await supabase
            .from('event_attendees')
            .select('event_id, events:events!event_attendees_event_id_fkey(*)')
            .eq('user_id', user.id)
            .eq('role', 'interested');
            
          if (error) throw error;
          eventList = data.map(att => att.events).filter(Boolean);
          
        } else if (activeTab === 'hosting') {
          // Fetch events created by the user
          const { data, error } = await supabase
            .from('events')
            .select('*')
            .eq('created_by', user.id);
            
          if (error) throw error;
          eventList = data;
          
        } else if (activeTab === 'past') {
          // Fetch past events the user attended
          const today = new Date().toISOString().split('T')[0];
          const { data, error } = await supabase
            .from('event_attendees')
            .select('event_id, events:events!event_attendees_event_id_fkey(*)')
            .eq('user_id', user.id)
            .lt('events.date', today);
            
          if (error) throw error;
          eventList = data.map(att => att.events).filter(Boolean);
          
        } else {
          // Fetch all events with attendee count
          const { data, error } = await supabase
            .from('events_with_attendees')
            .select('*');
            
          if (error) throw error;
          eventList = data;
        }
        
        // Process event banner URLs if needed
        const processedEvents = await Promise.all(eventList.map(async (event) => {
          if (event && event.event_banner_url && !event.event_banner_url.startsWith('http')) {
            // Get public URL for the banner if it's a storage path
            const { data } = supabase.storage
              .from('banners')
              .getPublicUrl(`event-banners/${event.event_banner_url.replace(/^event-banners\//, '')}`);
            return { ...event, event_banner_url: data?.publicUrl || event.event_banner_url };
          }
          return event;
        }));

        const baseEvents = processedEvents.filter(Boolean);

        // Enrich with attendee preview avatars & counts (similar to Discover EventGrid)
        const eventIds = baseEvents.map(e => e?.id).filter(Boolean);

        if (eventIds.length > 0) {
          const { data: attendeeRows, error: attendeesError } = await supabase
            .from('event_attendees')
            .select(`
              event_id,
              profiles:user_id (
                display_name,
                name,
                email,
                avatar_url
              )
            `)
            .in('event_id', eventIds);

          if (!attendeesError && attendeeRows) {
            const counts = {};
            const previews = {};

            (attendeeRows || []).forEach(row => {
              const eid = row.event_id;
              counts[eid] = (counts[eid] || 0) + 1;
              const prof = row.profiles || {};
              if (!previews[eid]) previews[eid] = [];
              if (previews[eid].length < 3) {
                const disp = prof.display_name || prof.name || prof.email || '?';
                previews[eid].push({
                  display_name: prof.display_name,
                  name: prof.name,
                  email: prof.email,
                  avatar_url: prof.avatar_url,
                  initial: (disp[0] || '?').toUpperCase(),
                });
              }
            });

            const enrichedEvents = baseEvents.map(ev => ({
              ...ev,
              attendee_count:
                typeof ev.attendee_count === 'number'
                  ? ev.attendee_count
                  : (counts[ev.id] || 0),
              previewAttendees: ev.previewAttendees || previews[ev.id] || [],
            }));

            setEvents(enrichedEvents);
          } else {
            setEvents(baseEvents);
          }
        } else {
          setEvents(baseEvents);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [activeTab, user]);

  if (loading) {
    return (
      <div className="event-grid">
        <p>Loading events...</p>
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="event-grid">
        <p className="no-events">No events to display yet.</p>
      </div>
    );
  }

  return (
    <div className="event-grid">
      {events.map(event => (
        <div
          key={event.id}
          className="event-card"
          onClick={() => setSelectedEvent(event)}
        >
          {event.event_banner_url && (
            <div className="event-card-image">
              <img src={event.event_banner_url} alt={event.title} />
            </div>
          )}
          {(event.previewAttendees && event.previewAttendees.length > 0) ||
          typeof event.attendee_count === 'number' ? (
            <div className="event-avatars">
              {(event.previewAttendees || []).slice(0, 3).map((p, idx) => (
                p.avatar_url ? (
                  <img
                    key={idx}
                    className="event-avatar img"
                    src={p.avatar_url}
                    alt={p.display_name || p.name || p.email || 'attendee'}
                    title={p.display_name || p.name || p.email}
                  />
                ) : (
                  <div
                    key={idx}
                    className="event-avatar"
                    title={p.display_name || p.name || p.email}
                  >
                    {(p.initial || p.display_name?.[0] || p.name?.[0] || p.email?.[0] || '?').toUpperCase()}
                  </div>
                )
              ))}
              {typeof event.attendee_count === 'number' &&
                event.attendee_count > (event.previewAttendees?.length || 0) && (
                  <div className="event-avatar more">
                    +{event.attendee_count - (event.previewAttendees?.length || 0)}
                  </div>
                )}
            </div>
          ) : null}
          <div className="event-card-content">
            <h3>{event.title}</h3>
            <p className="event-date">{event.date}</p>
            {event.time && <p className="event-time">{event.time}</p>}
            {event.status && (
              <div className={`event-status-chip ${event.status}`}>
                {event.status === 'cancelled'
                  ? 'Cancelled'
                  : event.status.charAt(0).toUpperCase() + event.status.slice(1)}
              </div>
            )}
            {(event.language || event.level) && (
              <p className="event-language">
                {event.language}
                {event.language && event.level && ' • '}
                {event.level && `Level: ${event.level}`}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventList;